import axios from 'axios'
import { differenceInCalendarDays, parseISO, startOfDay, subMonths } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useFormatDate } from '@/composables/useFormatDate'
import { tableLayout } from '@/services/analyticsTable'
import { EMPTY_CELL, formatCell, formatEt } from '@/services/numberFormat'
import { useTransactionStore } from './transaction'

/**
 * RED-310 — "Analüütika" drawer on /transactions (MaRu price statistics
 * clone). One store owns the form, the request lifecycle, the result and the
 * two secondary actions (Excel export, TSV copy) so the drawer, the form and
 * the result components stay thin. With 2+ regions picked it also loads each
 * region's own result and its CSV (RED-370).
 *
 * Request/response shapes follow the frozen API contract in the RED-310 plan
 * (`.claude/plans/red-310-*.md` → "Frozen API contract"). Field names here are
 * the wire names — never rename them client-side.
 */

export const ANALYTICS_STATES = Object.freeze([
  'IDLE',
  'LOADING',
  'RESULT',
  'RESULT_PARTIAL',
  'TOO_FEW',
  'EMPTY',
  'TOO_MANY',
  'ERROR',
])

export const OPTIONS_URL = '/api/transactions/analytics/options'
export const PRICE_STATISTICS_URL = '/api/transactions/analytics/price-statistics'
export const EXPORT_URL = '/transactions/analytics/price-statistics/export'
export const EXPORT_FALLBACK_FILENAME = 'hinnastatistika.xlsx'
/** RED-370: the same statistics per selected region (JSON) and as one CSV file. */
export const REGIONS_URL = '/api/transactions/analytics/price-statistics/regions'
export const REGIONS_EXPORT_URL = '/transactions/analytics/price-statistics/regions/export'
export const REGIONS_EXPORT_FALLBACK_FILENAME = 'hinnastatistika-piirkonniti.csv'

/** Data older than this (days) gets the amber "Andmed seisuga" hint. */
export const STALE_AFTER_DAYS = 60

/**
 * Map filter keys every report overrides — never sent, never shown as chips.
 * A report adds its own through `reports[].forced_filters` (RED-316: land
 * fixes `hoonestatud`).
 */
export const FORCED_MAP_FILTER_KEYS = Object.freeze([
  'objekti_liik',
  'startDate',
  'endDate',
  'viewport',
])

export const REPORT_KORTERIOMANDID = 'korteriomandid'

/** Separator inside the composite option keys of the location pickers. */
const KEY_SEPARATOR = '\u001f'

const unitKey = (...parts) => parts.map(p => p ?? '').join(KEY_SEPARATOR)

const defaultForm = () => ({
  report: REPORT_KORTERIOMANDID,
  locationMode: 'units', // 'units' | 'bbox'
  maakond: [], // option objects from `maakondOptions`
  omavalitsus: [], // option objects from `omavalitsusOptions`
  asum: [], // option objects from `asumOptions` (linnaosa or asum)
  periodMode: 'range', // 'range' | 'previous_month' | 'current_year' | 'previous_year' | 'multi'
  from: null, // Date
  to: null, // Date
  step: 'quarter', // 'month' | 'quarter' | 'year'
  count: 4, // 2..8
  trim: 0, // 0..10 (integer %)
  useMapFilters: true,
  groupBy: 'band', // 'band' | 'period'
})

const extractFilename = contentDisposition => {
  if (!contentDisposition) return null
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match) return decodeURIComponent(utf8Match[1])
  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
  return plainMatch ? plainMatch[1] : null
}

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const isCancelled = error =>
  axios.isCancel?.(error) ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError'

/** A request payload that names two or more location units. */
const isMultiRegion = payload =>
  payload?.location?.mode === 'units' && (payload.location.units?.length ?? 0) >= 2

export const useAnalyticsStore = defineStore('analytics', () => {
  const transactionStore = useTransactionStore()
  const { formatDatePickerToISO, formatISOToDatePicker, formatDate } =
    useFormatDate()

  // ── State ──────────────────────────────────────────────────────────
  const isOpen = ref(false)
  const state = ref('IDLE')
  const options = ref(null)
  const optionsLoading = ref(false)
  const result = ref(null)
  const form = ref(defaultForm())
  const formCollapsed = ref(false)
  const exporting = ref(false)
  const error = ref(null)

  /** Payload of the result currently on screen (export/copy reuse it). */
  const lastPayload = ref(null)

  /**
   * RED-370: one `{unit, location, result}` per selected region, loaded after
   * a table result when 2+ regions are picked. `regionsState`: IDLE | LOADING
   * | READY | ERROR.
   */
  const regions = ref(null)
  const regionsState = ref('IDLE')
  const regionsError = ref(null)
  const exportingCsv = ref(false)
  const csvFailed = ref(false)

  let prefilled = false
  let runAbortController = null
  let regionsAbortController = null
  let optionsPromise = null

  // ── Selected report ────────────────────────────────────────────────
  /** Options entry of the chosen report (`multi`, `group_label`, `forced_filters`, `population`). */
  const selectedReport = computed(
    () => reportOptions.value.find(r => r.key === form.value.report) ?? null,
  )

  /** False for reports without period comparison (RED-316 land). */
  const multiAllowed = computed(() => selectedReport.value?.multi !== false)

  const forcedMapFilterKeys = computed(() => [
    ...FORCED_MAP_FILTER_KEYS,
    ...(selectedReport.value?.forced_filters ?? []),
  ])

  /**
   * Deal count of a tree node for the chosen report. `n` is keyed by
   * population (RED-318): reports that read the same deals share one count.
   */
  const countOf = n => {
    if (n === null || typeof n !== 'object') return n ?? 0
    return n[selectedReport.value?.population ?? form.value.report] ?? 0
  }

  // ── Map coupling (read-only) ───────────────────────────────────────
  const mapFilterList = computed(() =>
    Object.values(transactionStore.transactionMapRequestParams.filters ?? {}),
  )

  /** Map filters the report actually forwards (forced keys dropped). */
  const inheritedMapFilters = computed(() =>
    mapFilterList.value.filter(f => !forcedMapFilterKeys.value.includes(f?.key)),
  )

  /** EPSG:3301 extent `[minX, minY, maxX, maxY]` or null. */
  const mapBbox = computed(
    () => transactionStore.transactionMapRequestParams.bbox ?? null,
  )

  const mapDateRange = computed(() => {
    const filters = transactionStore.transactionMapRequestParams.filters ?? {}
    return {
      from: filters.startDate?.value ?? null,
      to: filters.endDate?.value ?? null,
    }
  })

  /** Approximate visible map area in km (width × height), or null. */
  const mapBboxSizeKm = computed(() => {
    const bbox = mapBbox.value
    if (!Array.isArray(bbox) || bbox.length !== 4) return null
    const [minX, minY, maxX, maxY] = bbox.map(Number)
    if ([minX, minY, maxX, maxY].some(Number.isNaN)) return null
    return {
      width: Math.abs(maxX - minX) / 1000,
      height: Math.abs(maxY - minY) / 1000,
    }
  })

  // ── Options tree → picker options ──────────────────────────────────
  // Every node carries its count for the chosen report; places without a
  // single deal of that report are hidden.
  const maakondOptions = computed(() =>
    (options.value?.tree ?? [])
      .map(m => ({
        key: unitKey(m.maakond),
        label: m.maakond,
        n: countOf(m.n),
        maakond: m.maakond,
      }))
      .filter(m => m.n > 0),
  )

  const omavalitsusOptions = computed(() => {
    const chosen = new Set(form.value.maakond.map(m => m.maakond))
    return (options.value?.tree ?? [])
      .filter(m => chosen.has(m.maakond))
      .map(m => ({
        label: m.maakond,
        maakond: m.maakond,
        items: (m.omavalitsused ?? [])
          .map(o => ({
            key: unitKey(m.maakond, o.label),
            label: o.label,
            n: countOf(o.n),
            maakond: m.maakond,
            omavalitsus: o.label,
          }))
          .filter(o => o.n > 0),
      }))
      .filter(group => group.items.length > 0)
  })

  const asumOptions = computed(() => {
    const chosen = new Set(form.value.omavalitsus.map(o => o.key))
    const groups = []
    for (const m of options.value?.tree ?? []) {
      for (const o of m.omavalitsused ?? []) {
        if (!chosen.has(unitKey(m.maakond, o.label))) continue
        const items = []
        for (const u of o.units ?? []) {
          if (countOf(u.n) === 0) continue
          const isLinnaosa = u.kind === 'linnaosa'
          items.push({
            key: unitKey(m.maakond, o.label, isLinnaosa ? u.label : '', isLinnaosa ? '' : u.label),
            label: u.label,
            n: countOf(u.n),
            kind: u.kind,
            maakond: m.maakond,
            omavalitsus: o.label,
            linnaosa: isLinnaosa ? u.label : null,
            asum: isLinnaosa ? null : u.label,
          })
          for (const a of u.asumid ?? []) {
            if (countOf(a.n) === 0) continue
            items.push({
              key: unitKey(m.maakond, o.label, isLinnaosa ? u.label : '', a.label),
              label: a.label,
              n: countOf(a.n),
              kind: 'asum',
              parent: u.label,
              maakond: m.maakond,
              omavalitsus: o.label,
              linnaosa: isLinnaosa ? u.label : null,
              asum: a.label,
            })
          }
        }
        if (items.length) {
          groups.push({ label: o.label, key: unitKey(m.maakond, o.label), items })
        }
      }
    }
    return groups
  })

  const hasAsumOptions = computed(() => asumOptions.value.length > 0)

  const reportOptions = computed(() => {
    const reports = options.value?.reports
    if (Array.isArray(reports) && reports.length) {
      return reports.map(r => ({ ...r, disabled: !r.enabled }))
    }
    return [{ key: REPORT_KORTERIOMANDID, label: null, enabled: true, disabled: false }]
  })

  const trimOptions = computed(() => {
    const values = Array.isArray(options.value?.trim) && options.value.trim.length
      ? options.value.trim
      : Array.from({ length: 11 }, (_, i) => i)
    return values.map(v => ({ value: v, label: `${v} %` }))
  })

  /** One-line echo of the chosen units: "Harju maakond · Tallinn · Mustamäe linnaosa". */
  const locationSummary = computed(() => {
    const f = form.value
    if (f.locationMode === 'bbox') return null
    const parts = [
      f.maakond.map(m => m.label).join(', '),
      f.omavalitsus.map(o => o.label).join(', '),
      f.asum.map(a => a.label).join(', '),
    ].filter(Boolean)
    return parts.length ? parts.join(' · ') : null
  })

  // ── Derived flags ──────────────────────────────────────────────────
  const isLoading = computed(() => state.value === 'LOADING')

  const isComparison = computed(
    () => (result.value?.periods?.length ?? 0) > 1,
  )

  /** RED-370: the result on screen covers 2+ regions, so it gets a per-region table. */
  const hasRegions = computed(() => isMultiRegion(lastPayload.value))

  /** RED-322: an admin switched the company to every REdata company's deals (options `pool`). */
  const isRedataPool = computed(() => options.value?.pool === 'redata')

  const isStale = computed(() => {
    const asOf = result.value?.data_as_of
    if (!asOf) return false
    const date = parseISO(asOf)
    if (Number.isNaN(date.getTime())) return false
    return differenceInCalendarDays(new Date(), date) > STALE_AFTER_DAYS
  })

  const rangeValid = computed(() => {
    const f = form.value
    if (f.periodMode !== 'range') return true
    return !!f.from && !!f.to && startOfDay(f.from) <= startOfDay(f.to)
  })

  const canRun = computed(() => {
    const f = form.value
    if (state.value === 'LOADING') return false
    if (f.locationMode === 'bbox' && !mapBbox.value) return false
    return rangeValid.value
  })

  /** "Kogu maakond" quick action makes sense only when narrower than a county. */
  const canWholeCounty = computed(() => {
    const f = form.value
    return (
      f.locationMode === 'units' &&
      f.maakond.length > 0 &&
      (f.omavalitsus.length > 0 || f.asum.length > 0)
    )
  })

  // ── Form helpers ───────────────────────────────────────────────────
  const prefillFromMap = () => {
    const today = startOfDay(new Date())
    const { from, to } = mapDateRange.value
    form.value.to = to ? formatISOToDatePicker(to) : today
    form.value.from = from ? formatISOToDatePicker(from) : subMonths(today, 12)
  }

  /** Drop omavalitsus/asum picks whose parent is no longer selected. */
  const pruneChildren = () => {
    const f = form.value
    const maakonnad = new Set(f.maakond.map(m => m.maakond))
    f.omavalitsus = f.omavalitsus.filter(o => maakonnad.has(o.maakond))
    const omavalitsused = new Set(f.omavalitsus.map(o => unitKey(o.maakond, o.omavalitsus)))
    f.asum = f.asum.filter(a => omavalitsused.has(unitKey(a.maakond, a.omavalitsus)))
  }

  const setPeriodMode = mode => {
    form.value.periodMode = mode
    form.value.groupBy = mode === 'multi' ? 'period' : 'band'
  }

  /**
   * Switch the report: picks the new report hides (no deals there) are
   * dropped, and a report without period comparison leaves "Mitu ajavahemikku".
   */
  const setReport = key => {
    const f = form.value
    f.report = key
    const shown = new Set([
      ...maakondOptions.value.map(o => o.key),
      ...omavalitsusOptions.value.flatMap(group => group.items.map(o => o.key)),
      ...asumOptions.value.flatMap(group => group.items.map(o => o.key)),
    ])
    f.maakond = f.maakond.filter(o => shown.has(o.key))
    f.omavalitsus = f.omavalitsus.filter(o => shown.has(o.key))
    f.asum = f.asum.filter(o => shown.has(o.key))
    pruneChildren()
    if (!multiAllowed.value && f.periodMode === 'multi') setPeriodMode('range')
  }

  /** Cross product of the picks → `location.units` paths (plan contract). */
  const buildLocationUnits = () => {
    const f = form.value
    const units = []
    for (const m of f.maakond) {
      const omavalitsused = f.omavalitsus.filter(o => o.maakond === m.maakond)
      if (!omavalitsused.length) {
        units.push({ maakond: m.maakond })
        continue
      }
      for (const o of omavalitsused) {
        const picks = f.asum.filter(
          a => a.maakond === o.maakond && a.omavalitsus === o.omavalitsus,
        )
        if (!picks.length) {
          units.push({ maakond: o.maakond, omavalitsus: o.omavalitsus })
          continue
        }
        for (const a of picks) {
          const unit = { maakond: a.maakond, omavalitsus: a.omavalitsus }
          if (a.linnaosa) unit.linnaosa = a.linnaosa
          if (a.asum) unit.asum = a.asum
          units.push(unit)
        }
      }
    }
    return units
  }

  const buildPayload = () => {
    const f = form.value
    const location =
      f.locationMode === 'bbox'
        ? { mode: 'bbox', bbox: (mapBbox.value ?? []).map(Number) }
        : { mode: 'units', units: buildLocationUnits() }

    const period = { mode: f.periodMode }
    if (f.periodMode === 'range') {
      period.from = formatDatePickerToISO(f.from)
      period.to = formatDatePickerToISO(f.to)
    } else if (f.periodMode === 'multi') {
      period.step = f.step
      period.count = Number(f.count)
      if (f.to) period.anchor = formatDatePickerToISO(f.to)
    }

    const payload = {
      report: f.report,
      location,
      period,
      trim_percent: Number(f.trim),
      use_map_filters: !!f.useMapFilters,
    }
    if (f.useMapFilters) {
      payload.map_filters = inheritedMapFilters.value.map(filter => ({ ...filter }))
    }
    return payload
  }

  // ── Actions ────────────────────────────────────────────────────────
  const loadOptions = () => {
    if (options.value) return Promise.resolve(options.value)
    if (optionsPromise) return optionsPromise
    optionsLoading.value = true
    optionsPromise = axios
      .get(OPTIONS_URL)
      .then(({ data }) => {
        options.value = data
        return data
      })
      .catch(e => {
        console.warn('[analytics] options failed', e?.message ?? e)
        return null
      })
      .finally(() => {
        optionsLoading.value = false
        optionsPromise = null
      })
    return optionsPromise
  }

  const open = () => {
    isOpen.value = true
    if (!prefilled) {
      prefillFromMap()
      prefilled = true
    }
    loadOptions()
  }

  const close = () => {
    isOpen.value = false
  }

  const run = async () => {
    if (!canRun.value && state.value !== 'LOADING') return null
    runAbortController?.abort()
    const controller = new AbortController()
    runAbortController = controller

    const payload = buildPayload()
    state.value = 'LOADING'
    error.value = null
    clearRegions()

    try {
      const { data } = await axios.post(PRICE_STATISTICS_URL, payload, {
        signal: controller.signal,
      })
      if (controller !== runAbortController) return null
      result.value = data
      lastPayload.value = payload
      state.value = ANALYTICS_STATES.includes(data?.state) ? data.state : 'ERROR'
      formCollapsed.value = true
      if ((state.value === 'RESULT' || state.value === 'RESULT_PARTIAL') && isMultiRegion(payload)) {
        loadRegions(payload)
      }
      return data
    } catch (e) {
      if (isCancelled(e) || controller !== runAbortController) return null
      error.value = e?.response?.data?.message ?? e?.message ?? 'error'
      state.value = 'ERROR'
      return null
    } finally {
      if (controller === runAbortController) runAbortController = null
    }
  }

  /** Drop the per-region table and cancel its request (a new run or a reset). */
  const clearRegions = () => {
    regionsAbortController?.abort()
    regionsAbortController = null
    regions.value = null
    regionsState.value = 'IDLE'
    regionsError.value = null
    csvFailed.value = false
  }

  /**
   * RED-370: each region of the result on screen on its own. Loaded after
   * the combined result, so one region picked never asks for it.
   */
  const loadRegions = async (payload = lastPayload.value) => {
    if (!isMultiRegion(payload)) return null
    regionsAbortController?.abort()
    const controller = new AbortController()
    regionsAbortController = controller
    regionsState.value = 'LOADING'
    regionsError.value = null

    try {
      const { data } = await axios.post(REGIONS_URL, payload, { signal: controller.signal })
      if (controller !== regionsAbortController) return null
      regions.value = Array.isArray(data?.regions) ? data.regions : []
      regionsState.value = 'READY'
      return regions.value
    } catch (e) {
      if (isCancelled(e) || controller !== regionsAbortController) return null
      regionsError.value = e?.response?.data?.message ?? null
      regionsState.value = 'ERROR'
      return null
    } finally {
      if (controller === regionsAbortController) regionsAbortController = null
    }
  }

  /** RED-370: the per-region rows as one CSV file. False when it failed. */
  const exportCsv = async () => {
    if (exportingCsv.value || !hasRegions.value) return false
    exportingCsv.value = true
    csvFailed.value = false
    try {
      const response = await axios.post(REGIONS_EXPORT_URL, lastPayload.value, {
        responseType: 'blob',
      })
      const filename =
        extractFilename(response.headers?.['content-disposition']) ||
        REGIONS_EXPORT_FALLBACK_FILENAME
      downloadBlob(new Blob([response.data], { type: 'text/csv' }), filename)
      return true
    } catch (e) {
      console.warn('[analytics] csv export failed', e?.message ?? e)
      csvFailed.value = true
      return false
    } finally {
      exportingCsv.value = false
    }
  }

  const reset = () => {
    runAbortController?.abort()
    runAbortController = null
    clearRegions()
    form.value = defaultForm()
    prefillFromMap()
    result.value = null
    lastPayload.value = null
    error.value = null
    state.value = 'IDLE'
    formCollapsed.value = false
  }

  /** TOO_FEW quick action: widen the period by 12 months and re-run. */
  const extendTwelveMonths = () => {
    const f = form.value
    if (f.periodMode !== 'range') {
      const periods = result.value?.periods ?? []
      const first = periods[0]?.from
      const last = periods[periods.length - 1]?.to
      f.to = last ? parseISO(last) : startOfDay(new Date())
      f.from = first ? parseISO(first) : subMonths(f.to, 12)
      setPeriodMode('range')
    }
    f.from = subMonths(f.from ?? startOfDay(new Date()), 12)
    return run()
  }

  /** TOO_FEW quick action: keep the county, drop the narrower units, re-run. */
  const wholeCounty = () => {
    if (!canWholeCounty.value) return null
    form.value.omavalitsus = []
    form.value.asum = []
    return run()
  }

  const exportExcel = async () => {
    if (exporting.value) return false
    exporting.value = true
    try {
      const payload = lastPayload.value ?? buildPayload()
      const response = await axios.post(EXPORT_URL, payload, {
        responseType: 'blob',
      })
      const filename =
        extractFilename(response.headers?.['content-disposition']) ||
        EXPORT_FALLBACK_FILENAME
      downloadBlob(new Blob([response.data]), filename)
      return true
    } catch (e) {
      console.warn('[analytics] export failed', e?.message ?? e)
      return false
    } finally {
      exporting.value = false
    }
  }

  const bandRow = (row, layout) =>
    [
      row?.label ?? '',
      formatCell(row, 'count'),
      ...layout.valueColumns.map(column => formatCell(row, column.field, column.dp)),
    ].join('\t')

  const changeCell = value =>
    value === null || value === undefined ? EMPTY_CELL : `${formatEt(value, 1)} %`

  /** Plain-text TSV of the shown result (Excel / Sheets paste-ready). */
  const buildTsv = () => {
    const r = result.value
    if (!r?.periods?.length) return ''
    const lines = []
    if (r.title?.report) lines.push(r.title.report)
    const subtitle = [r.title?.location, r.title?.period].filter(Boolean).join(' ')
    if (subtitle) lines.push(subtitle)
    if (r.generated_at) lines.push(`Koostatud ${formatDate(r.generated_at)}`)
    lines.push('')

    // Copy header labels verbatim from design-spec §5.1, units per result (RED-318).
    const layout = tableLayout(r.table, r.columns)
    const multi = r.periods.length > 1
    r.periods.forEach((period, index) => {
      if (multi) {
        if (index > 0) lines.push('')
        const change = changeCell(period.change_pct_median)
        lines.push(`${period.label}\tMuutus (mediaan) %\t${change}`)
      }
      lines.push(layout.tsvHeader(r.table?.group_header ?? 'Pindala (m²)').join('\t'))
      for (const band of period.bands ?? []) lines.push(bandRow(band, layout))
      if (period.total && r.table?.total_row !== false) lines.push(bandRow(period.total, layout))
    })

    lines.push('')
    if (r.summary) {
      lines.push(r.summary)
      lines.push('')
    }
    for (const note of r.footnotes ?? []) lines.push(note)
    if (r.data_as_of) lines.push(`Andmed seisuga ${formatDate(r.data_as_of)}`)
    return lines.join('\n')
  }

  const copyTsv = async () => {
    const tsv = buildTsv()
    if (!tsv || !navigator.clipboard?.writeText) return false
    try {
      await navigator.clipboard.writeText(tsv)
      return true
    } catch (e) {
      console.warn('[analytics] copy failed', e?.message ?? e)
      return false
    }
  }

  return {
    // state
    isOpen,
    state,
    options,
    optionsLoading,
    result,
    form,
    formCollapsed,
    exporting,
    error,
    lastPayload,
    regions,
    regionsState,
    regionsError,
    exportingCsv,
    csvFailed,
    // map coupling
    mapFilterList,
    inheritedMapFilters,
    mapBbox,
    mapBboxSizeKm,
    mapDateRange,
    // options
    maakondOptions,
    omavalitsusOptions,
    asumOptions,
    hasAsumOptions,
    reportOptions,
    selectedReport,
    multiAllowed,
    forcedMapFilterKeys,
    trimOptions,
    locationSummary,
    // flags
    isLoading,
    isComparison,
    hasRegions,
    isRedataPool,
    isStale,
    rangeValid,
    canRun,
    canWholeCounty,
    // actions
    open,
    close,
    loadOptions,
    prefillFromMap,
    pruneChildren,
    setPeriodMode,
    setReport,
    buildPayload,
    run,
    reset,
    extendTwelveMonths,
    wholeCounty,
    exportExcel,
    loadRegions,
    exportCsv,
    buildTsv,
    copyTsv,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAnalyticsStore, import.meta.hot))
}
